import { Button } from 'antd';
import { KeenSliderPlugin, useKeenSlider } from 'keen-slider/react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { FC, useEffect, useMemo, useState } from 'react';
import { ComparableBoatCard } from '../ComparableBoatCard';
import { useComparableBoats, useGetActiveCarouselItem, useSetActiveCarouselItem } from '../hooks/useCompare';
import styles from './CompareCarousel.module.scss';
import { CompareCarouselProps } from './CompareCarousel.props';

const MutationPlugin: KeenSliderPlugin = (slider) => {
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      slider.update();
    });
  });

  const config = { childList: true };

  slider.on('created', () => {
    observer.observe(slider.container, config);
  });
  slider.on('destroyed', () => {
    observer.disconnect();
  });
};

const CompareCarousel: FC<CompareCarouselProps> = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const comparableBoats = useComparableBoats();
  const activeCarouselItem = useGetActiveCarouselItem();
  const setActiveCarouselItem = useSetActiveCarouselItem();

  const [ref, instanceRef] = useKeenSlider<HTMLDivElement>(
    {
      drag: false,
      slides: {
        perView: 7,
      },
      breakpoints: {
        '(max-width: 1440px)': {
          slides: { perView: 6 },
        },
        '(max-width: 1200px)': {
          slides: { perView: 5 },
        },
        '(max-width: 1024px)': {
          slides: { perView: 4 },
        },
        '(max-width: 992px)': {
          slides: { perView: 3 },
        },
        '(max-width: 768px)': {
          slides: { perView: 2 },
        },
        '(max-width: 520px)': {
          slides: { perView: 2 },
        },
      },
      slideChanged(slider) {
        setCurrentSlide(slider.track.details.rel);
        setActiveCarouselItem(slider.track.details.rel);
      },
      created() {
        setLoaded(true);
      },
    },
    [MutationPlugin],
  );

  useEffect(() => {
    instanceRef.current.moveToIdx(activeCarouselItem);
  }, [activeCarouselItem, instanceRef]);

  const hasPrevious = useMemo(() => currentSlide > 0, [currentSlide]);

  return (
    <div className={styles.compareCarouselWrapper}>
      {loaded && !!instanceRef.current && (
        <ul className={styles.actionsNav}>
          <li>
            {hasPrevious && (
              <Button
                disabled={!hasPrevious}
                type="text"
                className={styles.itemNavArrow}
                onClick={(e: any) => e.stopPropagation() || instanceRef.current?.prev()}
                icon={<ChevronLeft className={styles.itemNavArrowIcon} />}
              />
            )}
          </li>
          {instanceRef?.current?.track?.details?.slidesLength > 0.9 && (
            <li>
              <Button
                type="text"
                className={styles.itemNavArrow}
                icon={<ChevronRight className={styles.itemNavArrowIcon} />}
                onClick={(e: any) => e.stopPropagation() || instanceRef.current?.next()}
              />
            </li>
          )}
        </ul>
      )}
      <div ref={ref} className="keen-slider">
        {comparableBoats.map((boat, index) => (
          <ComparableBoatCard itemIndex={index} boat={boat} key={boat._id} />
        ))}
      </div>
    </div>
  );
};

export { CompareCarousel };
